/**
 * ====================================================================
 * FIKO CAD DIMENSION VISUAL STYLE SYSTEM
 * ====================================================================
 *
 * Profesyonel CAD görselleştirme stilleri - Merkezi konfigürasyon
 * CATIA, SolidWorks, AutoCAD preset'leri dahil
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 */

import * as THREE from 'three';

// ====================================================================
// EXTENDED ARROW STYLES
// ====================================================================

/**
 * Genişletilmiş ok stilleri - Profesyonel CAD sistemleri
 */
export enum ExtendedArrowStyle {
    // Temel stiller
    FILLED = 'FILLED',           // Dolu ok (CAD standardı)
    OPEN = 'OPEN',               // Açık ok (V şekli)
    DOT = 'DOT',                 // Nokta (daire)
    TICK = 'TICK',               // Çizgi (architectural)
    NONE = 'NONE',               // Ok yok

    // Profesyonel stiller
    ARCH = 'ARCH',               // Yay şeklinde ok (CATIA tarzı)
    BOX = 'BOX',                 // Kare ok (AutoCAD)
    OBLIQUE = 'OBLIQUE',         // Eğik ok (30°)
    SLASH = 'SLASH',             // / şeklinde (architectural)
    DOUBLE = 'DOUBLE',           // >> çift ok
    INTEGRAL = 'INTEGRAL',       // ∫ integral işareti
    DATUM = 'DATUM',             // ▽ datum üçgen
    ORIGIN = 'ORIGIN'            // ⊙ origin işareti
}

// ====================================================================
// LINE STYLE TYPES
// ====================================================================

/**
 * Çizgi stil tipleri
 */
export enum LineStyleType {
    SOLID = 'SOLID',             // Düz çizgi
    DASHED = 'DASHED',           // Kesikli
    DOTTED = 'DOTTED',           // Noktalı
    DASH_DOT = 'DASH_DOT',       // Çizgi-nokta
    DASH_DOT_DOT = 'DASH_DOT_DOT', // Çizgi-nokta-nokta
    CENTER = 'CENTER',           // Merkez çizgi deseni
    PHANTOM = 'PHANTOM',         // Fantom çizgi
    CONSTRUCTION = 'CONSTRUCTION', // Yapı çizgisi
    HIDDEN = 'HIDDEN'            // Gizli çizgi
}

// ====================================================================
// COLOR PALETTE
// ====================================================================

/**
 * Dimension renk paleti
 */
export interface DimensionColorPalette {
    normal: number;
    hovered: number;
    selected: number;
    dragging: number;
    error: number;
    glowColor: number;
    extensionLine: number;
    textColor: string;
    textBackground: string;
    // v2.0.0 - Ek renk alanları
    dimensionLine?: number;      // Ölçü çizgisi rengi (normal ile aynı varsayılan)
    leaderLine?: number;         // Leader çizgi rengi
}

// ====================================================================
// STYLE CONFIGURATIONS
// ====================================================================

/**
 * Ok stili konfigürasyonu
 */
export interface ArrowStyleConfig {
    type: ExtendedArrowStyle;
    size: number;
    aspectRatio: number;
    hollowThickness?: number;    // OPEN stil için
    curvature?: number;          // ARCH stil için
    angle?: number;              // OBLIQUE stil için (derece)
    filled?: boolean;            // Dolu mu boş mu
    strokeWidth?: number;        // Çerçeve kalınlığı
}

/**
 * Çizgi stili konfigürasyonu
 */
export interface LineStyleConfig {
    type: LineStyleType;
    width: number;
    dashPattern?: number[];      // [dash, gap, dash, gap...]
    dashOffset?: number;
    gradientColors?: [number, number];
    opacity: number;
    lineCap?: 'butt' | 'round' | 'square';
    lineJoin?: 'round' | 'bevel' | 'miter';
}

/**
 * Label stili konfigürasyonu
 */
export interface LabelStyleConfig {
    fontFamily: string;
    fontSize: number;
    fontWeight: 'normal' | 'bold' | 'light';
    fontStyle: 'normal' | 'italic';
    textColor: string;
    backgroundColor?: string;
    backgroundOpacity?: number;
    borderColor?: string;
    borderWidth?: number;
    borderRadius: number;
    padding: { x: number; y: number };
    shadow: {
        enabled: boolean;
        color: string;
        blur: number;
        offset: { x: number; y: number };
    };
    outline: {
        enabled: boolean;
        color: string;
        width: number;
    };
}

/**
 * Glow efekti konfigürasyonu
 */
export interface GlowEffectConfig {
    enabled: boolean;
    color?: number;
    intensity?: number;
    radius?: number;
    scale?: number;
    pulseSpeed?: number;         // 0 = no pulse
    blendMode?: 'additive' | 'normal';
    // v2.0.0 - Durum bazlı glow
    onHover?: boolean;           // Hover'da glow aktif mi
    onSelect?: boolean;          // Selection'da glow aktif mi
}

/**
 * Animasyon konfigürasyonu
 */
export interface AnimationConfig {
    enabled: boolean;
    fadeInDuration?: number;
    fadeOutDuration?: number;
    hoverTransitionDuration?: number;
    dragFeedbackScale?: number;
    snapIndicatorDuration?: number;
    pulseOnChange?: boolean;
    pulseDuration?: number;
    pulseColor?: number;
    easing?: 'linear' | 'easeIn' | 'easeOut' | 'easeInOut' | 'elastic';
    // v2.0.0 - Kısa erişim özellikleri
    duration?: number;           // Genel animasyon süresi (fadeInDuration ile aynı)
    fadeIn?: boolean;            // Fade-in animasyonu aktif mi
    fadeOut?: boolean;           // Fade-out animasyonu aktif mi
}

/**
 * Tooltip konfigürasyonu
 */
export interface TooltipConfig {
    enabled: boolean;
    showDelay?: number;
    hideDelay?: number;
    backgroundColor?: string;
    textColor?: string;
    fontSize?: number;
    borderRadius?: number;
    padding?: { x: number; y: number };
    showDimensionType?: boolean;
    showTolerance?: boolean;
    showId?: boolean;
    maxWidth?: number;
    position?: 'cursor' | 'dimension' | 'fixed';
    offset?: { x: number; y: number };
}

/**
 * Leader line konfigürasyonu
 */
export interface LeaderLineConfig {
    style: 'straight' | 'curved' | 'broken' | 'spline';
    tension: number;             // Spline tension (0-1)
    segments: number;            // Curve segments
    breakDistance: number;       // Broken style için
    breakAngle: number;          // Broken style için (radyan)
    arrowAtEnd: boolean;
    arrowAtStart: boolean;
    arrowStyle: ExtendedArrowStyle;
    arrowSize: number;
    lineStyle: LineStyleConfig;
}

/**
 * Extension line konfigürasyonu
 */
export interface ExtensionLineConfig {
    visible: boolean;
    gap: number;                 // Geometriden gap
    overshoot: number;           // Dimension line'ı geçme
    style: LineStyleConfig;
    oblique: boolean;            // Eğik mi
    obliqueAngle: number;        // Eğiklik açısı (derece)
}

/**
 * Smart placement konfigürasyonu
 */
export interface SmartPlacementConfig {
    enabled: boolean;
    avoidOverlap: boolean;
    avoidOcclusion: boolean;
    preferredDirections: ('up' | 'down' | 'left' | 'right')[];
    maxSearchSteps: number;
    stepSize: number;            // piksel
    autoRotateText: boolean;     // Yazıyı okunaklı tut
    keepTextUpright: boolean;    // Baş aşağı olmasın
}

// ====================================================================
// MAIN VISUAL SETTINGS
// ====================================================================

/**
 * Ana görsel ayarlar - Tüm dimension özellikleri
 */
export interface DimensionVisualSettings {
    // Renk paleti
    colors: DimensionColorPalette;

    // Ok ayarları
    arrow: ArrowStyleConfig;
    arrowSecondary?: ArrowStyleConfig;  // Farklı ikinci ok

    // Çizgi ayarları
    dimensionLine: LineStyleConfig;
    extensionLine: ExtensionLineConfig;
    leaderLine: LeaderLineConfig;

    // Label ayarları
    label: LabelStyleConfig;

    // Efektler
    glow: GlowEffectConfig;
    animation: AnimationConfig;
    tooltip: TooltipConfig;

    // Akıllı yerleşim
    smartPlacement: SmartPlacementConfig;

    // Genel
    scale: {
        mode: 'fixed' | 'screen' | 'world';
        fixedSize: number;
        minSize: number;
        maxSize: number;
        referenceDistance: number;
    };

    // Render ayarları
    render: {
        depthTest: boolean;
        depthWrite: boolean;
        transparent: boolean;
        renderOrder: number;
        antialias: boolean;
    };

    // v2.0.0 - Kısa erişim özellikleri (opsiyonel)
    arrowStyle?: ExtendedArrowStyle;   // arrow.type ile aynı
    lineStyle?: LineStyleType;          // dimensionLine.type ile aynı
}

// ====================================================================
// PRESET STYLES
// ====================================================================

/**
 * Varsayılan renk paleti
 */
export const DEFAULT_COLOR_PALETTE: DimensionColorPalette = {
    normal: 0x000000,
    hovered: 0x0066CC,
    selected: 0xFF6600,
    dragging: 0x00AA00,
    error: 0xFF0000,
    glowColor: 0x4488FF,
    extensionLine: 0x666666,
    textColor: '#000000',
    textBackground: 'rgba(255, 255, 255, 0.9)'
};

/**
 * 🎯 v3.2.0: CATIA stil preset'i - SİYAH çizgi/ok, BEYAZ yazı, SİYAH arkaplan
 */
export const CATIA_STYLE_PRESET: DimensionVisualSettings = {
    colors: {
        normal: 0x000000,        // 🎯 SİYAH
        hovered: 0x333333,       // Koyu gri
        selected: 0xFF8800,
        dragging: 0xFFFF00,
        error: 0xFF0000,
        glowColor: 0x000000,     // 🎯 SİYAH
        extensionLine: 0x000000, // 🎯 SİYAH
        textColor: '#ffffff',    // 🎯 BEYAZ
        textBackground: 'rgba(0, 0, 0, 1)' // 🎯 SİYAH arkaplan
    },
    arrow: {
        type: ExtendedArrowStyle.FILLED,
        size: 0.8,
        aspectRatio: 2.5,
        filled: true
    },
    dimensionLine: {
        type: LineStyleType.SOLID,
        width: 1.5,
        opacity: 1,
        lineCap: 'round',
        lineJoin: 'round'
    },
    extensionLine: {
        visible: true,
        gap: 1,
        overshoot: 1.5,
        style: {
            type: LineStyleType.SOLID,
            width: 1,
            opacity: 0.8,
            lineCap: 'round',
            lineJoin: 'round'
        },
        oblique: false,
        obliqueAngle: 0
    },
    leaderLine: {
        style: 'broken',
        tension: 0.5,
        segments: 32,
        breakDistance: 10,
        breakAngle: Math.PI / 4,
        arrowAtEnd: true,
        arrowAtStart: false,
        arrowStyle: ExtendedArrowStyle.FILLED,
        arrowSize: 0.8,
        lineStyle: {
            type: LineStyleType.SOLID,
            width: 1.5,
            opacity: 1
        }
    },
    label: {
        fontFamily: 'ISOCPEUR, Arial, sans-serif',
        fontSize: 12,
        fontWeight: 'normal',
        fontStyle: 'normal',
        textColor: '#ffffff',    // 🎯 BEYAZ
        backgroundColor: 'rgba(0, 0, 0, 1)', // 🎯 SİYAH arkaplan
        backgroundOpacity: 1,
        borderColor: '#000000',  // 🎯 SİYAH
        borderWidth: 0.5,
        borderRadius: 2,
        padding: { x: 4, y: 2 },
        shadow: {
            enabled: false,
            color: 'rgba(0,0,0,0.3)',
            blur: 4,
            offset: { x: 1, y: 1 }
        },
        outline: {
            enabled: false,
            color: '#FFFFFF',
            width: 2
        }
    },
    glow: {
        enabled: false,          // 🎯 Glow kapalı
        color: 0x000000,
        intensity: 0.4,
        radius: 1.2,
        scale: 1.3,
        blendMode: 'additive'
    },
    animation: {
        enabled: true,
        fadeInDuration: 200,
        fadeOutDuration: 150,
        hoverTransitionDuration: 100,
        dragFeedbackScale: 1.05,
        snapIndicatorDuration: 200,
        pulseOnChange: true,
        pulseDuration: 400,
        pulseColor: 0x000000,    // 🎯 SİYAH
        easing: 'easeOut'
    },
    tooltip: {
        enabled: true,
        showDelay: 500,
        hideDelay: 100,
        backgroundColor: 'rgba(40, 40, 40, 0.95)',
        textColor: '#FFFFFF',
        fontSize: 11,
        borderRadius: 4,
        padding: { x: 8, y: 6 },
        showDimensionType: true,
        showTolerance: true,
        showId: false,
        maxWidth: 220,
        position: 'cursor',
        offset: { x: 12, y: 12 }
    },
    smartPlacement: {
        enabled: true,
        avoidOverlap: true,
        avoidOcclusion: true,
        preferredDirections: ['up', 'right', 'down', 'left'],
        maxSearchSteps: 12,
        stepSize: 15,
        autoRotateText: true,
        keepTextUpright: true
    },
    scale: {
        mode: 'screen',
        fixedSize: 1,
        minSize: 0.5,
        maxSize: 2,
        referenceDistance: 100
    },
    render: {
        depthTest: false,
        depthWrite: false,
        transparent: true,
        renderOrder: 999,
        antialias: true
    }
};

/**
 * SolidWorks stil preset'i
 */
export const SOLIDWORKS_STYLE_PRESET: DimensionVisualSettings = {
    colors: {
        normal: 0x000000,        // 🎯 SİYAH çizgi/ok
        hovered: 0x333333,       // Koyu gri (hover)
        selected: 0x000000,      // 🎯 SİYAH
        dragging: 0x000000,      // 🎯 SİYAH
        error: 0x000000,         // 🎯 SİYAH
        glowColor: 0x000000,     // 🎯 SİYAH
        extensionLine: 0x000000, // 🎯 SİYAH
        textColor: '#ffffff',    // 🎯 BEYAZ yazı
        textBackground: 'rgba(0, 0, 0, 1)' // 🎯 SİYAH arkaplan
    },
    arrow: {
        type: ExtendedArrowStyle.FILLED,
        size: 0.6,
        aspectRatio: 3,
        filled: true
    },
    dimensionLine: {
        type: LineStyleType.SOLID,
        width: 1,
        opacity: 1
    },
    extensionLine: {
        visible: true,
        gap: 0.5,
        overshoot: 1,
        style: {
            type: LineStyleType.SOLID,
            width: 0.75,
            opacity: 1
        },
        oblique: false,
        obliqueAngle: 0
    },
    leaderLine: {
        style: 'straight',
        tension: 0.5,
        segments: 16,
        breakDistance: 8,
        breakAngle: Math.PI / 4,
        arrowAtEnd: true,
        arrowAtStart: false,
        arrowStyle: ExtendedArrowStyle.FILLED,
        arrowSize: 0.6,
        lineStyle: {
            type: LineStyleType.SOLID,
            width: 1,
            opacity: 1
        }
    },
    label: {
        fontFamily: 'Century Gothic, Arial, sans-serif',
        fontSize: 10,
        fontWeight: 'normal',
        fontStyle: 'normal',
        textColor: '#ffffff',    // 🎯 BEYAZ yazı
        backgroundColor: 'rgba(0, 0, 0, 1)', // 🎯 SİYAH arkaplan
        backgroundOpacity: 1,
        borderRadius: 0,
        padding: { x: 3, y: 1 },
        shadow: {
            enabled: false,
            color: 'rgba(0,0,0,0)',
            blur: 0,
            offset: { x: 0, y: 0 }
        },
        outline: {
            enabled: false,
            color: '#000000',
            width: 0
        }
    },
    glow: {
        enabled: false,          // 🎯 Glow kapalı
        color: 0x000000,
        intensity: 0,
        radius: 0,
        scale: 1,
        blendMode: 'additive'
    },
    animation: {
        enabled: true,
        fadeInDuration: 150,
        fadeOutDuration: 100,
        hoverTransitionDuration: 80,
        dragFeedbackScale: 1.03,
        snapIndicatorDuration: 150,
        pulseOnChange: false,
        pulseDuration: 300,
        pulseColor: 0x000000,    // 🎯 SİYAH
        easing: 'easeOut'
    },
    tooltip: {
        enabled: true,
        showDelay: 600,
        hideDelay: 100,
        backgroundColor: 'rgba(0, 0, 0, 0.95)', // 🎯 SİYAH arkaplan
        textColor: '#ffffff',    // 🎯 BEYAZ yazı
        fontSize: 10,
        borderRadius: 3,
        padding: { x: 6, y: 4 },
        showDimensionType: true,
        showTolerance: true,
        showId: false,
        maxWidth: 200,
        position: 'cursor',
        offset: { x: 10, y: 10 }
    },
    smartPlacement: {
        enabled: true,
        avoidOverlap: true,
        avoidOcclusion: true,
        preferredDirections: ['up', 'right', 'down', 'left'],
        maxSearchSteps: 10,
        stepSize: 12,
        autoRotateText: true,
        keepTextUpright: true
    },
    scale: {
        mode: 'screen',
        fixedSize: 1,
        minSize: 0.4,
        maxSize: 1.8,
        referenceDistance: 100
    },
    render: {
        depthTest: false,
        depthWrite: false,
        transparent: true,
        renderOrder: 999,
        antialias: true
    }
};

/**
 * AutoCAD stil preset'i
 */
export const AUTOCAD_STYLE_PRESET: DimensionVisualSettings = {
    colors: {
        normal: 0x000000,        // 🎯 SİYAH çizgi/ok
        hovered: 0x333333,       // Koyu gri (hover)
        selected: 0x000000,      // 🎯 SİYAH
        dragging: 0x000000,      // 🎯 SİYAH
        error: 0x000000,         // 🎯 SİYAH
        glowColor: 0x000000,     // 🎯 SİYAH
        extensionLine: 0x000000, // 🎯 SİYAH
        textColor: '#ffffff',    // 🎯 BEYAZ yazı
        textBackground: 'rgba(0, 0, 0, 1)' // 🎯 SİYAH arkaplan
    },
    arrow: {
        type: ExtendedArrowStyle.TICK,
        size: 0.5,
        aspectRatio: 1,
        angle: 45
    },
    dimensionLine: {
        type: LineStyleType.SOLID,
        width: 1,
        opacity: 1
    },
    extensionLine: {
        visible: true,
        gap: 0.625,
        overshoot: 0.625,
        style: {
            type: LineStyleType.SOLID,
            width: 1,
            opacity: 1
        },
        oblique: false,
        obliqueAngle: 0
    },
    leaderLine: {
        style: 'spline',
        tension: 0.3,
        segments: 24,
        breakDistance: 10,
        breakAngle: Math.PI / 6,
        arrowAtEnd: true,
        arrowAtStart: false,
        arrowStyle: ExtendedArrowStyle.FILLED,
        arrowSize: 0.5,
        lineStyle: {
            type: LineStyleType.SOLID,
            width: 1,
            opacity: 1
        }
    },
    label: {
        fontFamily: 'Romans, Simplex, Arial, sans-serif',
        fontSize: 9,
        fontWeight: 'normal',
        fontStyle: 'normal',
        textColor: '#ffffff',    // 🎯 BEYAZ yazı
        backgroundColor: 'rgba(0, 0, 0, 1)', // 🎯 SİYAH arkaplan
        backgroundOpacity: 1,
        borderRadius: 0,
        padding: { x: 2, y: 1 },
        shadow: {
            enabled: false,
            color: 'rgba(0,0,0,0)',
            blur: 0,
            offset: { x: 0, y: 0 }
        },
        outline: {
            enabled: false,
            color: '#000000',
            width: 0
        }
    },
    glow: {
        enabled: false,
        color: 0x000000,         // 🎯 SİYAH
        intensity: 0,
        radius: 0,
        scale: 1,
        blendMode: 'additive'
    },
    animation: {
        enabled: false,
        fadeInDuration: 0,
        fadeOutDuration: 0,
        hoverTransitionDuration: 50,
        dragFeedbackScale: 1,
        snapIndicatorDuration: 100,
        pulseOnChange: false,
        pulseDuration: 0,
        pulseColor: 0x000000,    // 🎯 SİYAH
        easing: 'linear'
    },
    tooltip: {
        enabled: false,
        showDelay: 800,
        hideDelay: 100,
        backgroundColor: 'rgba(0, 0, 0, 0.95)', // 🎯 SİYAH arkaplan
        textColor: '#ffffff',    // 🎯 BEYAZ yazı
        fontSize: 9,
        borderRadius: 2,
        padding: { x: 4, y: 2 },
        showDimensionType: false,
        showTolerance: true,
        showId: false,
        maxWidth: 180,
        position: 'cursor',
        offset: { x: 8, y: 8 }
    },
    smartPlacement: {
        enabled: false,
        avoidOverlap: false,
        avoidOcclusion: false,
        preferredDirections: ['up', 'right'],
        maxSearchSteps: 6,
        stepSize: 10,
        autoRotateText: false,
        keepTextUpright: false
    },
    scale: {
        mode: 'world',
        fixedSize: 1,
        minSize: 0.3,
        maxSize: 3,
        referenceDistance: 100
    },
    render: {
        depthTest: true,
        depthWrite: true,
        transparent: false,
        renderOrder: 0,
        antialias: false
    }
};

/**
 * FikoYT özel stil preset'i (Modern, kullanıcı dostu)
 */
export const FIKOYT_STYLE_PRESET: DimensionVisualSettings = {
    colors: {
        normal: 0x000000,        // 🎯 SİYAH çizgi/ok
        hovered: 0x333333,       // Koyu gri (hover)
        selected: 0x000000,      // 🎯 SİYAH
        dragging: 0x000000,      // 🎯 SİYAH
        error: 0x000000,         // 🎯 SİYAH
        glowColor: 0x000000,     // 🎯 SİYAH
        extensionLine: 0x000000, // 🎯 SİYAH
        textColor: '#ffffff',    // 🎯 BEYAZ yazı
        textBackground: 'rgba(0, 0, 0, 1)' // 🎯 SİYAH arkaplan
    },
    arrow: {
        type: ExtendedArrowStyle.FILLED,
        size: 0.7,
        aspectRatio: 2.2,
        filled: true
    },
    dimensionLine: {
        type: LineStyleType.SOLID,
        width: 1.5,
        opacity: 1,
        lineCap: 'round',
        lineJoin: 'round'
    },
    extensionLine: {
        visible: true,
        gap: 1,
        overshoot: 1.2,
        style: {
            type: LineStyleType.SOLID,
            width: 1,
            opacity: 0.7,
            lineCap: 'round'
        },
        oblique: false,
        obliqueAngle: 0
    },
    leaderLine: {
        style: 'curved',
        tension: 0.4,
        segments: 32,
        breakDistance: 12,
        breakAngle: Math.PI / 5,
        arrowAtEnd: true,
        arrowAtStart: false,
        arrowStyle: ExtendedArrowStyle.FILLED,
        arrowSize: 0.7,
        lineStyle: {
            type: LineStyleType.SOLID,
            width: 1.5,
            opacity: 1,
            lineCap: 'round'
        }
    },
    label: {
        fontFamily: 'Segoe UI, Roboto, Arial, sans-serif',
        fontSize: 11,
        fontWeight: 'normal',
        fontStyle: 'normal',
        textColor: '#ffffff',    // 🎯 BEYAZ yazı
        backgroundColor: 'rgba(0, 0, 0, 1)', // 🎯 SİYAH arkaplan
        backgroundOpacity: 1,
        borderColor: '#000000',  // 🎯 SİYAH
        borderWidth: 1,
        borderRadius: 4,
        padding: { x: 6, y: 3 },
        shadow: {
            enabled: false,      // 🎯 Gölge kapalı
            color: 'rgba(0,0,0,0)',
            blur: 0,
            offset: { x: 0, y: 0 }
        },
        outline: {
            enabled: false,
            color: '#000000',
            width: 0
        }
    },
    glow: {
        enabled: false,          // 🎯 Glow kapalı
        color: 0x000000,
        intensity: 0,
        radius: 0,
        scale: 1,
        pulseSpeed: 0,
        blendMode: 'additive'
    },
    animation: {
        enabled: true,
        fadeInDuration: 250,
        fadeOutDuration: 180,
        hoverTransitionDuration: 120,
        dragFeedbackScale: 1.08,
        snapIndicatorDuration: 250,
        pulseOnChange: true,
        pulseDuration: 500,
        pulseColor: 0x000000,    // 🎯 SİYAH
        easing: 'easeInOut'
    },
    tooltip: {
        enabled: true,
        showDelay: 400,
        hideDelay: 80,
        backgroundColor: 'rgba(0, 0, 0, 0.95)', // 🎯 SİYAH arkaplan
        textColor: '#ffffff',    // 🎯 BEYAZ yazı
        fontSize: 12,
        borderRadius: 6,
        padding: { x: 10, y: 8 },
        showDimensionType: true,
        showTolerance: true,
        showId: true,
        maxWidth: 280,
        position: 'cursor',
        offset: { x: 15, y: 15 }
    },
    smartPlacement: {
        enabled: true,
        avoidOverlap: true,
        avoidOcclusion: true,
        preferredDirections: ['up', 'right', 'down', 'left'],
        maxSearchSteps: 15,
        stepSize: 12,
        autoRotateText: true,
        keepTextUpright: true
    },
    scale: {
        mode: 'screen',
        fixedSize: 1,
        minSize: 0.5,
        maxSize: 2.5,
        referenceDistance: 100
    },
    render: {
        depthTest: false,
        depthWrite: false,
        transparent: true,
        renderOrder: 1000,
        antialias: true
    }
};

/**
 * Minimal stil preset'i (Temiz, basit)
 */
export const MINIMAL_STYLE_PRESET: DimensionVisualSettings = {
    colors: {
        normal: 0x000000,        // 🎯 SİYAH çizgi/ok
        hovered: 0x333333,       // Koyu gri (hover)
        selected: 0x000000,      // 🎯 SİYAH
        dragging: 0x000000,      // 🎯 SİYAH
        error: 0x000000,         // 🎯 SİYAH
        glowColor: 0x000000,     // 🎯 SİYAH
        extensionLine: 0x000000, // 🎯 SİYAH
        textColor: '#ffffff',    // 🎯 BEYAZ yazı
        textBackground: 'rgba(0, 0, 0, 1)' // 🎯 SİYAH arkaplan
    },
    arrow: {
        type: ExtendedArrowStyle.TICK,
        size: 0.4,
        aspectRatio: 1,
        angle: 45
    },
    dimensionLine: {
        type: LineStyleType.SOLID,
        width: 1,
        opacity: 1
    },
    extensionLine: {
        visible: true,
        gap: 0.5,
        overshoot: 0.5,
        style: {
            type: LineStyleType.SOLID,
            width: 0.5,
            opacity: 0.6
        },
        oblique: false,
        obliqueAngle: 0
    },
    leaderLine: {
        style: 'straight',
        tension: 0.5,
        segments: 8,
        breakDistance: 6,
        breakAngle: Math.PI / 4,
        arrowAtEnd: true,
        arrowAtStart: false,
        arrowStyle: ExtendedArrowStyle.TICK,
        arrowSize: 0.4,
        lineStyle: {
            type: LineStyleType.SOLID,
            width: 1,
            opacity: 1
        }
    },
    label: {
        fontFamily: 'system-ui, sans-serif',
        fontSize: 10,
        fontWeight: 'light',
        fontStyle: 'normal',
        textColor: '#ffffff',    // 🎯 BEYAZ yazı
        backgroundColor: 'rgba(0, 0, 0, 1)', // 🎯 SİYAH arkaplan
        backgroundOpacity: 1,
        borderRadius: 0,
        padding: { x: 2, y: 0 },
        shadow: {
            enabled: false,
            color: 'rgba(0,0,0,0)',
            blur: 0,
            offset: { x: 0, y: 0 }
        },
        outline: {
            enabled: false,
            color: '#000000',
            width: 0
        }
    },
    glow: {
        enabled: false,
        color: 0x000000,         // 🎯 SİYAH
        intensity: 0,
        radius: 0,
        scale: 1,
        blendMode: 'additive'
    },
    animation: {
        enabled: false,
        fadeInDuration: 0,
        fadeOutDuration: 0,
        hoverTransitionDuration: 0,
        dragFeedbackScale: 1,
        snapIndicatorDuration: 0,
        pulseOnChange: false,
        pulseDuration: 0,
        pulseColor: 0x000000,    // 🎯 SİYAH
        easing: 'linear'
    },
    tooltip: {
        enabled: false,
        showDelay: 0,
        hideDelay: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.95)', // 🎯 SİYAH arkaplan
        textColor: '#ffffff',    // 🎯 BEYAZ yazı
        fontSize: 10,
        borderRadius: 0,
        padding: { x: 0, y: 0 },
        showDimensionType: false,
        showTolerance: false,
        showId: false,
        maxWidth: 150,
        position: 'cursor',
        offset: { x: 5, y: 5 }
    },
    smartPlacement: {
        enabled: false,
        avoidOverlap: false,
        avoidOcclusion: false,
        preferredDirections: ['up'],
        maxSearchSteps: 4,
        stepSize: 8,
        autoRotateText: false,
        keepTextUpright: true
    },
    scale: {
        mode: 'fixed',
        fixedSize: 1,
        minSize: 0.8,
        maxSize: 1.2,
        referenceDistance: 100
    },
    render: {
        depthTest: true,
        depthWrite: false,
        transparent: true,
        renderOrder: 500,
        antialias: true
    }
};

// ====================================================================
// DEFAULT SETTINGS
// ====================================================================

/**
 * Varsayılan görsel ayarlar - FikoYT preset'i kullanılır
 */
export const DEFAULT_VISUAL_SETTINGS: DimensionVisualSettings = { ...FIKOYT_STYLE_PRESET };

// ====================================================================
// PRESET MAP
// ====================================================================

/**
 * Preset isimleri
 */
export type PresetName = 'catia' | 'solidworks' | 'autocad' | 'fikoyt' | 'minimal' | 'custom';

/**
 * Preset haritası
 */
export const STYLE_PRESETS: Record<Exclude<PresetName, 'custom'>, DimensionVisualSettings> = {
    catia: CATIA_STYLE_PRESET,
    solidworks: SOLIDWORKS_STYLE_PRESET,
    autocad: AUTOCAD_STYLE_PRESET,
    fikoyt: FIKOYT_STYLE_PRESET,
    minimal: MINIMAL_STYLE_PRESET
};

// ====================================================================
// HELPER FUNCTIONS
// ====================================================================

/**
 * Preset al
 */
export function getStylePreset(name: PresetName): DimensionVisualSettings {
    if (name === 'custom') {
        return { ...DEFAULT_VISUAL_SETTINGS };
    }
    return { ...STYLE_PRESETS[name] };
}

/**
 * Kısmi güncelleme ile preset birleştir
 */
export function mergeWithPreset(
    presetName: PresetName,
    overrides: Partial<DimensionVisualSettings>
): DimensionVisualSettings {
    const preset = getStylePreset(presetName);
    return deepMerge(preset, overrides) as DimensionVisualSettings;
}

/**
 * Deep merge helper
 */
function deepMerge<T extends object>(target: T, source: Partial<T>): T {
    const result = { ...target };

    for (const key in source) {
        if (source.hasOwnProperty(key)) {
            const sourceValue = source[key];
            const targetValue = target[key];

            if (
                sourceValue !== undefined &&
                typeof sourceValue === 'object' &&
                !Array.isArray(sourceValue) &&
                sourceValue !== null &&
                typeof targetValue === 'object' &&
                !Array.isArray(targetValue) &&
                targetValue !== null
            ) {
                (result as any)[key] = deepMerge(targetValue as object, sourceValue as object);
            } else if (sourceValue !== undefined) {
                (result as any)[key] = sourceValue;
            }
        }
    }

    return result;
}

/**
 * Renk paleti güncelle
 */
export function updateColorPalette(
    settings: DimensionVisualSettings,
    palette: Partial<DimensionColorPalette>
): DimensionVisualSettings {
    return {
        ...settings,
        colors: { ...settings.colors, ...palette }
    };
}

/**
 * Animasyonları etkinleştir/devre dışı bırak
 */
export function setAnimationsEnabled(
    settings: DimensionVisualSettings,
    enabled: boolean
): DimensionVisualSettings {
    return {
        ...settings,
        animation: { ...settings.animation, enabled }
    };
}

/**
 * Glow efektini etkinleştir/devre dışı bırak
 */
export function setGlowEnabled(
    settings: DimensionVisualSettings,
    enabled: boolean
): DimensionVisualSettings {
    return {
        ...settings,
        glow: { ...settings.glow, enabled }
    };
}

/**
 * Tooltip'i etkinleştir/devre dışı bırak
 */
export function setTooltipEnabled(
    settings: DimensionVisualSettings,
    enabled: boolean
): DimensionVisualSettings {
    return {
        ...settings,
        tooltip: { ...settings.tooltip, enabled }
    };
}

/**
 * Smart placement'ı etkinleştir/devre dışı bırak
 */
export function setSmartPlacementEnabled(
    settings: DimensionVisualSettings,
    enabled: boolean
): DimensionVisualSettings {
    return {
        ...settings,
        smartPlacement: { ...settings.smartPlacement, enabled }
    };
}
